import * as Sentry from '@sentry/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  APIError,
  APIErrorType,
  type GetProductCategoriesQuery,
  type GetProductCategoriesQueryVariables,
  useCreateUnlistedPdpMutation,
  useGetProductCategoriesQuery,
  useUploadImageMutation,
} from '@vizit/api';
import { SelectedFileStatus } from '@vizit/atoms';
import { DEFAULT_RETAILER_REGION_ID, SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { useDebounce } from '@vizit/hooks';
import type { CategoryItem } from '@vizit/molecules';
import type { CreateANewProductFormSchema } from '@vizit/organisms';
import { type S3UploadFields, uploadToS3 } from '@vizit/s3';
import isEmpty from 'lodash.isempty';
import * as React from 'react';
const PAGE_SIZE = 10;

interface UseProductCategoryListProps {
  /** Organization ID */
  organizationId?: string;
  /** Portfolio ID */
  portfolioId?: string;
  /** Function to handle authentication error */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Result interface for the product category list hook
 */
interface UseProductCategoryListResult {
  /** Array of category items with transformed breadcrumbs */
  items: CategoryItem[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Whether the next page is being fetched */
  isFetchingNextPage: boolean;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Current filter text for searching categories */
  filterText: string;
  /** Function to update the filter text */
  setFilterText: (text: string) => void;
  /** Function to load more items (next page) */
  loadMore: () => void;
}

interface UseProductNewDialogProps {
  /** Portfolio ID */
  portfolioId?: string;
  /** Organization ID */
  organizationId?: string;
  /** Function to call when the PDP is successfully submitted */
  onSuccessfulCreate: (pdpId: string) => void;
  /** Function to handle authentication error */
  onAuthenticationError: (error: APIError) => void;
}

function useProductNewDialog(props: UseProductNewDialogProps) {
  const { portfolioId, organizationId, onSuccessfulCreate, onAuthenticationError } = props;

  const [hasOnSubmissionError, setHasOnSubmissionError] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState<SelectedFileStatus | undefined>(SelectedFileStatus.Ready);

  const { mutateAsync: createNewProduct } = useCreateUnlistedPdpMutation();
  const { mutateAsync: uploadHeroImage } = useUploadImageMutation();

  const handleOnSubmit = async (data: CreateANewProductFormSchema) => {
    const retailerId = data.retailerId || DEFAULT_RETAILER_REGION_ID;

    try {
      const heroImageResponse = await uploadHeroImage({
        portfolio_id: portfolioId ?? '',
        image_type: 'hero',
        name: data.file.name,
        product_category_id: data.category,
        source: 'user_uploaded',
      });

      if (!heroImageResponse?.id || !heroImageResponse?.url || !heroImageResponse?.upload_fields) {
        throw new Error('Failed to upload hero image');
      }

      const uploadFields = JSON.parse(heroImageResponse.upload_fields) as S3UploadFields;

      // If upload to S3 is successful.
      const onSuccess = async () => {
        Sentry.addBreadcrumb({
          message: 'Asset upload completed.',
          level: 'info',
          data: { asset_id: heroImageResponse.id, organization_id: organizationId, portfolio_id: portfolioId },
        });

        setUploadStatus(SelectedFileStatus.Success);

        if (heroImageResponse?.id) {
          // Slight delay to ensure the upload status is set to success.
          const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
          await delay(200);
        }
      };

      // If upload to S3 fails.
      const onError = (error?: Error) => {
        setUploadStatus(SelectedFileStatus.Error);

        Sentry.captureException(error, {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Asset,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetS3Upload,
          },
          extra: {
            asset_id: heroImageResponse.id,
            file_name: data.file.name,
            file_size: data.file.size,
            portfolio_id: portfolioId,
            organization_id: organizationId,
            asset_type: 'hero',
          },
        });
      };

      await uploadToS3(heroImageResponse.id, heroImageResponse.url, data.file, uploadFields, onSuccess, onError);

      const response = await createNewProduct({
        portfolio_id: portfolioId ?? '',
        brand: data.brand ?? '',
        hero_image_id: heroImageResponse.id,
        product_category_id: data.category,
        retailer_region: retailerId,
        title: data.productTitle,
      });

      if (!response?.id) {
        throw new Error('Failed to create new product');
      }

      onSuccessfulCreate(response.id);
    } catch (error) {
      if (
        error instanceof APIError &&
        (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.NotAuthorized)
      ) {
        onAuthenticationError(error);
      } else {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Product,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productCreate,
          },
        });

        setHasOnSubmissionError(true);
      }
    }
  };

  return {
    hasOnSubmissionError,
    uploadStatus,
    onFileStatusChange: setUploadStatus,
    onSubmit: handleOnSubmit,
  };
}

/**
 * Hook for managing product category list with infinite scrolling and filtering
 * Uses TanStack Query for proper caching and background updates
 *
 * @returns Object containing category data and control functions
 */
function useProductCategoryList(props: UseProductCategoryListProps): UseProductCategoryListResult {
  const { organizationId, portfolioId, onAuthenticationError } = props;

  const [localFilterText, setLocalFilterText] = React.useState('');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  const queryVariables: Omit<GetProductCategoriesQueryVariables, 'offset'> = React.useMemo(() => {
    return {
      // @ts-ignore: this is protected by the router and will always have a value.
      organization_id: organizationId,
      // @ts-ignore: this is protected by the router and will always have a value.
      portfolio_id: portfolioId,
      breadcrumbs_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      limit: PAGE_SIZE,
      name_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      sort_by: 'name',
      sort_direction: 'asc',
    };
  }, [organizationId, portfolioId, debouncedFilterText]);

  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['GetProductCategoriesPages', queryVariables],
    queryFn: async ({ pageParam = 0 }) => {
      const response: GetProductCategoriesQuery = await useGetProductCategoriesQuery.fetcher({
        ...queryVariables,
        offset: pageParam,
      });

      const transformedCategories: CategoryItem[] = (response.data || []).map((category) => ({
        ...category,
        breadcrumbs: category.breadcrumbs.split(','),
      }));

      const hasData = response.data && response.data.length > 0;

      return {
        data: transformedCategories,
        // Only increment cursor if we received data, otherwise return undefined to indicate no more data
        // This was the glitch we were seeing on the internal demo 06.05.2025.
        nextCursor: hasData ? pageParam + 10 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!portfolioId,
  });

  // Flatten all pages into a single items array with a duplicate ID check
  const items = React.useMemo(() => {
    const allPageItems = data?.pages.flatMap((page) => page.data) || [];

    const uniqueItems = allPageItems.reduce((uniqueItems, item) => {
      if (uniqueItems.findIndex((existingItem) => existingItem.id === item.id) === -1) {
        uniqueItems.push(item);
      }
      return uniqueItems;
    }, [] as CategoryItem[]);

    return uniqueItems;
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  React.useEffect(() => {
    if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
      onAuthenticationError(error);
    }
  }, [error, onAuthenticationError]);

  return {
    items,
    isLoading: isLoading,
    isFetchingNextPage: isFetchingNextPage,
    hasNextPage: hasNextPage || false,
    filterText: localFilterText,
    setFilterText: setLocalFilterText,
    loadMore,
  };
}

export { useProductNewDialog, useProductCategoryList };
